import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, Send, Upload, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('inquiry'); // 'inquiry' or 'resume'
  
  // Contact Form State
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactStatus, setContactStatus] = useState(null);

  // Resume Form State
  const [resumeForm, setResumeForm] = useState({
    name: '',
    email: '',
    phone: '',
    linkedIn: '',
    resume: null
  });
  const [resumeSubmitting, setResumeSubmitting] = useState(false);
  const [resumeStatus, setResumeStatus] = useState(null);

  // Handle Contact Form Submit
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSubmitting(true);
    setContactStatus(null);

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactForm),
      });

      if (!response.ok) {
        throw new Error('Failed to submit');
      }

      setContactStatus({ type: 'success', message: t('contact.successContact') });
      setContactForm({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (error) {
      setContactStatus({ type: 'error', message: t('contact.errorContact') });
    } finally {
      setContactSubmitting(false);
    }
  };

  // Handle Resume Form Submit
  const handleResumeSubmit = async (e) => {
    e.preventDefault();
    setResumeSubmitting(true);
    setResumeStatus(null);

    try {
      const formData = new FormData();
      formData.append('name', resumeForm.name);
      formData.append('email', resumeForm.email);
      if (resumeForm.phone) formData.append('phone', resumeForm.phone);
      if (resumeForm.linkedIn) formData.append('linkedIn', resumeForm.linkedIn);
      formData.append('resume', resumeForm.resume);

      const response = await fetch(`${API_URL}/api/resume`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to submit resume');
      }

      setResumeStatus({ type: 'success', message: t('contact.successResume') });
      setResumeForm({ name: '', email: '', phone: '', linkedIn: '', resume: null });
      // Clear file input
      document.querySelector('input[type="file"]').value = '';
    } catch (error) {
      setResumeStatus({ type: 'error', message: t('contact.errorResume') });
    } finally {
      setResumeSubmitting(false);
    }
  };

  // Handle File Upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setResumeForm({ ...resumeForm, resume: file });
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-br from-white to-gray-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-50 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-50 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            {t('contact.title')} <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">{t('contact.titleHighlight')}</span>
          </h2>
          <p className="text-lg text-slate-700 leading-relaxed">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              <div className="p-8 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl backdrop-blur-sm">
                <h3 className="text-2xl font-bold text-slate-900 mb-6">{t('contact.getInTouch')}</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-cyan-600 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-slate-900" />
                    </div>
                    <div>
                      <div className="text-sm text-slate-700 mb-1">{t('contact.email')}</div>
                      <a href="mailto:info@tag-jobs.com" className="text-cyan-700 hover:text-cyan-600 transition-colors">
                        info@tag-jobs.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-cyan-600 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-slate-900" />
                    </div>
                    <div>
                      <div className="text-sm text-slate-700 mb-1">{t('contact.phone')}</div>
                      <a href="tel:+16082424242" className="text-cyan-700 hover:text-cyan-600 transition-colors block mb-2">
                        608 242 4242
                      </a>
                      <a href="tel:+18777937348" className="text-cyan-700 hover:text-cyan-600 transition-colors block">
                        <div>877 7 WE'RE IT</div>
                        <div className="text-sm">(877-793-7348)</div>
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-cyan-600 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-slate-900" />
                    </div>
                    <div>
                      <div className="text-sm text-slate-700 mb-1">{t('contact.location')}</div>
                      <div className="text-slate-700">
                        3905 State St<br />
                        Suite 7114<br />
                        Santa Barbara, CA 93105
                      </div>
                      <div className="text-slate-600 mt-3">
                        {t('contact.clients')} &nbsp;&nbsp;--&gt;&nbsp;&nbsp; {t('contact.clientsRegion')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-gray-200">
                  <div className="text-sm text-slate-700 mb-3">{t('contact.visitWebsite')}</div>
                  <a 
                    href="https://www.tag-jobs.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-cyan-700 hover:text-cyan-600 transition-colors font-medium"
                  >
                    www.tag-jobs.com
                  </a>
                </div>
              </div>
            </div>

            {/* Forms */}
            <div className="lg:col-span-2">
              {/* Tab Navigation */}
              <div className="flex space-x-2 mb-6">
                <button
                  onClick={() => setActiveTab('inquiry')}
                  className={`flex-1 px-6 py-3 rounded-xl font-bold transition-all duration-300 ${
                    activeTab === 'inquiry'
                      ? 'bg-gradient-to-r from-cyan-600 to-teal-600 text-slate-900 shadow-lg'
                      : 'bg-gray-50/50 text-slate-700 hover:text-cyan-600 border-2 border-gray-200'
                  }`}
                >
                  {t('contact.generalInquiry')}
                </button>
                <button
                  onClick={() => setActiveTab('resume')}
                  className={`flex-1 px-6 py-3 rounded-xl font-bold transition-all duration-300 ${
                    activeTab === 'resume'
                      ? 'bg-gradient-to-r from-cyan-600 to-teal-600 text-slate-900 shadow-lg'
                      : 'bg-gray-50/50 text-slate-700 hover:text-cyan-600 border-2 border-gray-200'
                  }`}
                >
                  {t('contact.submitResume')}
                </button>
              </div>

              {/* Contact Form */}
              {activeTab === 'inquiry' && (
                <div className="p-8 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl backdrop-blur-sm">
                  <form onSubmit={handleContactSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="contact-name" className="text-slate-700 mb-2">{t('contact.nameLabel')}</Label>
                        <Input
                          id="contact-name"
                          type="text"
                          required
                          value={contactForm.name}
                          onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                          className="bg-gray-50/50 border-gray-200 text-slate-900 focus:border-cyan-500 focus:ring-cyan-500/20"
                          placeholder={t('contact.namePlaceholder')}
                        />
                      </div>

                      <div>
                        <Label htmlFor="contact-email" className="text-slate-700 mb-2">{t('contact.emailLabel')}</Label>
                        <Input
                          id="contact-email"
                          type="email"
                          required
                          value={contactForm.email}
                          onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                          className="bg-gray-50/50 border-gray-200 text-slate-900 focus:border-cyan-500 focus:ring-cyan-500/20"
                          placeholder={t('contact.emailPlaceholder')}
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="contact-phone" className="text-slate-700 mb-2">{t('contact.phoneLabel')}</Label>
                        <Input
                          id="contact-phone"
                          type="tel"
                          value={contactForm.phone}
                          onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                          className="bg-gray-50/50 border-gray-200 text-slate-900 focus:border-cyan-500 focus:ring-cyan-500/20"
                          placeholder={t('contact.phonePlaceholder')}
                        />
                      </div>

                      <div>
                        <Label htmlFor="contact-company" className="text-slate-700 mb-2">{t('contact.companyLabel')}</Label>
                        <Input
                          id="contact-company"
                          type="text"
                          value={contactForm.company}
                          onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                          className="bg-gray-50/50 border-gray-200 text-slate-900 focus:border-cyan-500 focus:ring-cyan-500/20"
                          placeholder={t('contact.companyPlaceholder')}
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="contact-message" className="text-slate-700 mb-2">{t('contact.messageLabel')}</Label>
                      <Textarea
                        id="contact-message"
                        required
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        className="bg-gray-50/50 border-gray-200 text-slate-900 focus:border-cyan-500 focus:ring-cyan-500/20 min-h-[150px]"
                        placeholder={t('contact.messagePlaceholder')}
                      />
                    </div>

                    {/* Status Message */}
                    {contactStatus && (
                      <div className={`flex items-center space-x-2 p-4 rounded-lg ${
                        contactStatus.type === 'success' 
                          ? 'bg-green-900/30 border border-green-500/30 text-green-300' 
                          : 'bg-red-900/30 border border-red-500/30 text-red-300'
                      }`}>
                        {contactStatus.type === 'success' ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <AlertCircle className="w-5 h-5" />
                        )}
                        <span>{contactStatus.message}</span>
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={contactSubmitting}
                      className="w-full bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-500 hover:to-teal-500 text-slate-900 font-bold py-6 rounded-xl shadow-xl shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-300"
                    >
                      {contactSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          {t('contact.sending')}
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          {t('contact.sendMessage')}
                        </>
                      )}
                    </Button>
                  </form>
                </div>
              )}

              {/* Resume Form */}
              {activeTab === 'resume' && (
                <div className="p-8 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl backdrop-blur-sm">
                  <form onSubmit={handleResumeSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="resume-name" className="text-slate-700 mb-2">Name *</Label>
                        <Input
                          id="resume-name"
                          type="text"
                          required
                          value={resumeForm.name}
                          onChange={(e) => setResumeForm({ ...resumeForm, name: e.target.value })}
                          className="bg-gray-50/50 border-gray-200 text-slate-900 focus:border-cyan-500 focus:ring-cyan-500/20"
                          placeholder="Your full name"
                        />
                      </div>

                      <div>
                        <Label htmlFor="resume-email" className="text-slate-700 mb-2">Email *</Label>
                        <Input
                          id="resume-email"
                          type="email"
                          required
                          value={resumeForm.email}
                          onChange={(e) => setResumeForm({ ...resumeForm, email: e.target.value })}
                          className="bg-gray-50/50 border-gray-200 text-slate-900 focus:border-cyan-500 focus:ring-cyan-500/20"
                          placeholder="your.email@example.com"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="resume-phone" className="text-slate-700 mb-2">Phone *</Label>
                        <Input
                          id="resume-phone"
                          type="tel"
                          required
                          value={resumeForm.phone}
                          onChange={(e) => setResumeForm({ ...resumeForm, phone: e.target.value })}
                          className="bg-gray-50/50 border-gray-200 text-slate-900 focus:border-cyan-500 focus:ring-cyan-500/20"
                          placeholder="(555) 123-4567"
                        />
                      </div>

                      <div>
                        <Label htmlFor="resume-linkedin" className="text-slate-700 mb-2">LinkedIn Profile</Label>
                        <Input
                          id="resume-linkedin"
                          type="url"
                          value={resumeForm.linkedIn}
                          onChange={(e) => setResumeForm({ ...resumeForm, linkedIn: e.target.value })}
                          className="bg-gray-50/50 border-gray-200 text-slate-900 focus:border-cyan-500 focus:ring-cyan-500/20"
                          placeholder="https://linkedin.com/in/yourprofile"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="resume-file" className="text-slate-700 mb-2">Resume/CV *</Label>
                      <div className="relative">
                        <Input
                          id="resume-file"
                          type="file"
                          required
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange}
                          className="bg-gray-50/50 border-gray-200 text-slate-900 focus:border-cyan-500 focus:ring-cyan-500/20 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-cyan-600 file:text-slate-900 file:font-bold hover:file:bg-cyan-500 file:cursor-pointer"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Accepted formats: PDF, DOC, DOCX (Max 5MB)
                      </p>
                      {resumeForm.resume && (
                        <div className="flex items-center space-x-2 mt-3 text-cyan-700 text-sm">
                          <Upload className="w-4 h-4" />
                          <span>{resumeForm.resume.name}</span>
                        </div>
                      )}
                    </div>

                    {/* Status Message */}
                    {resumeStatus && (
                      <div className={`flex items-center space-x-2 p-4 rounded-lg ${
                        resumeStatus.type === 'success' 
                          ? 'bg-green-900/30 border border-green-500/30 text-green-300' 
                          : 'bg-red-900/30 border border-red-500/30 text-red-300'
                      }`}>
                        {resumeStatus.type === 'success' ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <AlertCircle className="w-5 h-5" />
                        )}
                        <span>{resumeStatus.message}</span>
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={resumeSubmitting}
                      className="w-full bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-500 hover:to-teal-500 text-slate-900 font-bold py-6 rounded-xl shadow-xl shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-300"
                    >
                      {resumeSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-5 h-5 mr-2" />
                          Submit Resume
                        </>
                      )}
                    </Button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
